import { useEffect } from "react";
import {
  registerSuperProperties,
  setUserProperties,
  type MixpanelSuperProperties,
} from "common/analytics";
import { useAuth } from "common/hooks";
import { useDarkMode } from "@/hooks/useDarkMode";
import { applyUserIdentity } from "@/analytics/identity";
import {
  resolveDeviceFormFactor,
  DEVICE_BREAKPOINT_QUERIES,
} from "@/analytics/device";

/** Map a browser PermissionState / Notification permission to the tracking-plan vocabulary. */
function normalizePermission(state: string): string {
  if (state === "granted") return "granted";
  if (state === "denied") return "denied";
  return "not_determined"; // "prompt" | "default" | anything unknown
}

/**
 * Query the browser for location + notification permission and register them as super properties.
 * Client-only; silently skips any permission the browser doesn't expose. Safe to call without a
 * provider (registerSuperProperties no-ops when unconfigured).
 */
export async function registerPermissionSuperProps(): Promise<void> {
  if (typeof window === "undefined") return;
  const props: Partial<MixpanelSuperProperties> = {};

  try {
    const permissions = (
      navigator as Navigator & {
        permissions?: {
          query: (d: { name: PermissionName }) => Promise<PermissionStatus>;
        };
      }
    ).permissions;
    if (permissions?.query) {
      const geo = await permissions.query({
        name: "geolocation" as PermissionName,
      });
      props.location_permission_state = normalizePermission(geo.state);
    }
  } catch {
    // Permissions API unsupported or blocked — leave location_permission_state unset.
  }

  if (typeof Notification !== "undefined" && Notification.permission) {
    props.notification_permission_state = normalizePermission(
      Notification.permission,
    );
  }

  if (Object.keys(props).length > 0) registerSuperProperties(props);
}

/**
 * Keeps the React-context-dependent super properties in sync with app state:
 *   - dark_mode  (theme toggle)
 *   - auth_state (logged_in / logged_out)
 * and the role-derived people property:
 *   - user_type  (user / mapper)
 * Also re-establishes Mixpanel identity (identify + base people props) for sessions restored
 * on boot, so identity doesn't ride solely on persisted SDK state.
 * Mount once near the app root (RootLayout). `device` + permission states are registered in
 * init.ts; `page_name` is registered on route change in __root.tsx.
 */
export function useAnalyticsSuperProperties(): void {
  const { isLoggedIn, isMapper, isBusinessUser, user, savedPlacesIds, savedPlacesData } =
    useAuth();
  const isDark = useDarkMode();

  // dark_mode — on mount + whenever the theme changes.
  useEffect(() => {
    registerSuperProperties({ dark_mode: !!isDark });
  }, [isDark]);

  // device — viewport form factor (mobile <768 / tablet <1024 / desktop), from the app's Tailwind
  // breakpoints. apps/web is responsive, so this is kept LIVE: a browser resize or tablet rotation
  // that crosses a breakpoint re-registers the new bucket. Seeded in init.ts; re-applied here on
  // mount, which also recovers it after logout's resetAnalytics() wipes super properties. The
  // matchMedia change events fire only on the breakpoint crossings, not on every resize pixel.
  useEffect(() => {
    const applyDevice = () =>
      registerSuperProperties({ device: resolveDeviceFormFactor() });
    applyDevice(); // initial + reset-recovery
    const mqls = DEVICE_BREAKPOINT_QUERIES.map((q) => window.matchMedia(q));
    mqls.forEach((mql) => mql.addEventListener("change", applyDevice));
    return () => {
      mqls.forEach((mql) => mql.removeEventListener("change", applyDevice));
    };
    // isLoggedIn is a re-trigger only (re-register after logout reset); not read in the effect.
    // (exhaustive-deps allows the extra dep — no disable needed.)
  }, [isLoggedIn]);

  // auth_state — super property; reacts to login/logout.
  useEffect(() => {
    registerSuperProperties({
      auth_state: isLoggedIn ? "logged_in" : "logged_out",
    });
  }, [isLoggedIn]);

  // user_type — now a people property (was a super property). Role-derived (user / mapper); set when
  // logged in so it writes to the identified profile, not the anonymous device profile. resetAnalytics()
  // on logout clears it; this re-applies it on the next login.
  useEffect(() => {
    if (isLoggedIn) {
      setUserProperties({ user_type: isMapper ? "mapper" : "user" });
    }
  }, [isLoggedIn, isMapper]);

  // location_permission_state + notification_permission_state — kept LIVE, not just boot-seeded
  // (init.ts seeds them once before mount). Re-runs on auth transitions so they are re-registered
  // after resetAnalytics() wipes super properties on logout. Geolocation exposes a real onchange;
  // Notification has no change event, so we re-query when the tab regains focus/visibility (covers
  // a permission flipped in browser settings while backgrounded, and the post-prompt grant/deny).
  useEffect(() => {
    const applyNotification = () => {
      if (typeof Notification !== "undefined" && Notification.permission) {
        registerSuperProperties({
          notification_permission_state: normalizePermission(Notification.permission),
        });
      }
    };
    applyNotification(); // initial + reset-recovery
    const onVisible = () => {
      if (document.visibilityState === "visible") applyNotification();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", applyNotification);

    let geoStatus: PermissionStatus | undefined;
    let cancelled = false;
    const permissions = (
      navigator as Navigator & {
        permissions?: {
          query: (d: { name: PermissionName }) => Promise<PermissionStatus>;
        };
      }
    ).permissions;
    if (permissions?.query) {
      permissions
        .query({ name: "geolocation" as PermissionName })
        .then((status) => {
          if (cancelled) return;
          geoStatus = status;
          const applyGeo = () =>
            registerSuperProperties({
              location_permission_state: normalizePermission(status.state),
            });
          applyGeo(); // initial + reset-recovery
          status.onchange = applyGeo; // live grant/revoke
        })
        .catch(() => {
          // Permissions API unsupported/blocked — the boot-time value stands.
        });
    }

    return () => {
      cancelled = true;
      if (geoStatus) geoStatus.onchange = null;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", applyNotification);
    };
    // isLoggedIn is a re-trigger only (re-register after logout reset); not read in the effect.
    // (exhaustive-deps allows the extra dep — no disable needed.)
  }, [isLoggedIn]);

  // business_owner (people property) — from the user's role flags (useAuth.isBusinessUser). Set
  // whenever it's known and the user is logged in; people props persist across the session.
  useEffect(() => {
    if (isLoggedIn && typeof isBusinessUser === "boolean") {
      setUserProperties({ business_owner: isBusinessUser });
    }
  }, [isLoggedIn, isBusinessUser]);

  // saved_places_count (people property) — total venues across all saved lists (matches the backend
  // definition: useAuth.savedPlacesIds is the flat list of every saved venue). Seeded from the login
  // payload in applyUserIdentity; this effect keeps it current as places are saved/removed. Gated on
  // savedPlacesData being loaded so we don't overwrite the seeded value with 0 before it loads.
  const savedPlacesCount = savedPlacesIds?.length ?? 0;
  useEffect(() => {
    if (isLoggedIn && savedPlacesData) {
      setUserProperties({ saved_places_count: savedPlacesCount });
    }
  }, [isLoggedIn, savedPlacesCount, savedPlacesData]);

  // Re-establish Mixpanel identity for a restored/authenticated session — not just inside the
  // login/register handlers. Without this, a session restored on boot sets auth_state="logged_in"
  // but never calls identify(), so identity rides entirely on mixpanel-browser's persisted state
  // (lost on a fresh device / cleared storage / private window). applyUserIdentity is idempotent;
  // keyed on the user id so it runs once per identified user. Logout's resetAnalytics() clears
  // identity and this effect skips while logged out.
  useEffect(() => {
    if (isLoggedIn && user?.id != null && String(user.id) !== "") {
      applyUserIdentity(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, user?.id]);
}
